import { SectionHandler } from '../entity.handler';
import { AssetUpdateRequest, AssetResponse, UserResponse } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { IKuiTableCol } from 'app/key-ui';
import { GridColumnHelper } from '../utils';
import { AssetGroupingService } from 'app/services';
import { ICONS, ASSET_ICONS } from '../icons';

export interface ListSectionValues {
    items: any;
}

export abstract class ListSectionHandler implements SectionHandler<any, ListSectionValues> {

    type = 'list';
    icon = '';
    bulk: false;

    title = this.translate('TITLE');
    description = this.translate(`DESC`);

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public visibleActions: ('add' | 'delete')[]
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.SHARED.LIST.${key}`, values);
    }

    applyUpdate(_entity: any, _payload: any): Promise<any> {
        return Promise.reject(new Error('applyUpdate not supported'));
    }

    mapEntityToEditor(_entity: any): Promise<ListSectionValues> {
        return Promise.reject(new Error('mapEntityToEditor not supported'));
    }

    mapEditorToEntity(_entity: AssetUpdateRequest, _values: ListSectionValues): Promise<AssetUpdateRequest> {
        return Promise.reject(new Error('mapEditorToEntity not supported'));
    }

    abstract getColumns(): Promise<IKuiTableCol[]>;
    abstract getItems(entity: any, offset: number, limit: number, bypassCache?: boolean): Promise<{ count: number, items: any[] }>;
    abstract actionClicked(action: string, items: any[]): Promise<any>;

}


export class AssetListSectionHandler extends ListSectionHandler {

    constructor(
        admin: AdminEditorService,
        ownerId: string,
        visibleActions: ('add' | 'delete')[],
        private grouping: AssetGroupingService,
        private filterField: string,
        private extraColumns: IKuiTableCol[],
        private section: { id: string, name: string },
        private getReplacementGroupFn: () => Promise<{ id: string, name: string }>,
        private getActionUpdate?: (action: 'add' | 'delete', field: any) => Promise<any>
    ) {
        super(admin, ownerId, visibleActions);
    }

    getColumns(): Promise<IKuiTableCol[]> {
        return Promise.all([
            this.grouping.getAssetTypes(this.ownerId),
        ]).then(([assetTypes]) => {
            const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.ASSET.COLUMNS');
            const columns: IKuiTableCol[] = [
                grid.IconTextColumn('name', 'NAME', row => {
                    let icon = ICONS.asset;
                    const at = assetTypes.find(x => x.id === row.assetType.id);
                    if (at) {
                        icon = ASSET_ICONS[at.data.tag] || icon;
                    }
                    return icon;
                }),
                ...this.extraColumns || [],
            ];
            return columns;
        });
    }

    getItems(entity: any, offset: number, limit: number, bypassCache?: boolean): Promise<{ count: number, items: any[] }> {
        return this.admin.getCached(`listAssets/${this.ownerId}/${offset}/${limit}/null/(state!=deleted),groups.id=${entity.id}/*`, api => api.entities.listAssets(this.ownerId, offset, limit, null, `(state!=deleted),${this.filterField}.id=${entity.id}/*`), null, bypassCache);
    }

    async actionClicked(action: string, items: AssetResponse[]): Promise<AssetResponse> {
        if (action === 'add') {
            const result = await this.admin.showFormModal({
                groups: [{
                    name: this.translate('ADD_ASSET'),
                    fields: [{ type: 'assetfilter', id: 'asset', title: this.translate('ASSET'), max: 1, options: { ownerId: this.ownerId } }],
                }],
            }, { asset: null });

            if (result && result.asset) {
                const asset = await this.admin.getCached(`getAsset/${result.asset}`, api => api.entities.getAsset(result.asset));
                let update = asset[this.filterField];

                if (this.getActionUpdate) {
                    update = await this.getActionUpdate(action, update);
                } else {
                    if (Array.isArray(update)) {
                        if (!update.find(x => x && x.id === this.section.id)) {
                            update.push(this.section);
                        }
                    } else {
                        update = this.section;
                    }
                }

                return this.admin.applyUpdate(api => api.entities.updateAsset(asset.id, { [this.filterField]: update }));
            }
        }
        if (action === 'delete') {
            let update = items[0][this.filterField];

            if (this.getActionUpdate) {
                update = await this.getActionUpdate(action, update);
            } else {
                if (Array.isArray(update)) {
                    // remove selected items
                    update.splice(update.findIndex(x => x && x.id === this.section.id), 1);

                    // if there are no more items left we should replace the one we just removed
                    if (!update.length) {
                        const replacement = await this.getReplacementGroupFn();
                        if (!replacement) { return null; }
                        update.push(replacement);
                    }
                } else {
                    const replacement = await this.getReplacementGroupFn();
                    if (!replacement) { return null; }
                    update = replacement;
                }
            }

            return this.admin.applyUpdate(api => api.entities.updateAsset(items[0].id, { [this.filterField]: update }));
        }
    }

}

export class UserListSectionHandler extends ListSectionHandler {

    constructor(
        admin: AdminEditorService,
        ownerId: string,
        visibleActions: ('add' | 'delete')[],
        private filterField: string,
        private extraColumns: IKuiTableCol[],
        private section: { id: string, name: string },
        private getReplacementGroupFn: () => Promise<{ id: string, name: string }>
    ) {
        super(admin, ownerId, visibleActions);
    }

    getColumns(): Promise<IKuiTableCol[]> {
        const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.USER.COLUMNS');
        const columns: IKuiTableCol[] = [
            grid.IconTextColumn('name', 'NAME', ICONS.user),
            ...this.extraColumns || [],
        ];
        return Promise.resolve(columns);
    }

    getItems(entity: any, offset: number, limit: number, bypassCache?: boolean): Promise<{ count: number, items: any[] }> {
        return this.admin.getCached(`listUsers/${this.ownerId}/${offset}/${limit}/null/groups.id=${entity.id}/*`,
            api => api.accounts.listUsers(this.ownerId, offset, limit /*, null, `${this.filterField}.id=${entity.id}/*`*/), null, bypassCache)
            .then(users => {
                // TODO: this should be handled by the API!!!!
                users.items = users.items.filter(x => x[this.filterField].id === entity.id);
                return users;
            });
    }

    // NOTE: this only supports object values for now... If we ever need to accomodate arrays and custom convertions please look at how AssetListSectionHandler.actionClicked is implemented
    async actionClicked(action: string, items: UserResponse[]): Promise<UserResponse> {
        if (action === 'add') {
            const result = await this.admin.showFormModal({
                groups: [{
                    name: this.translate('ADD_USER'),
                    fields: [{ type: 'userfilter', id: 'user', title: this.translate('USER'), max: 1, options: { ownerId: this.ownerId } }],
                }],
            }, { user: null });

            if (result && result.user) {
                const user = await this.admin.getCached(`getUser/${result.user.id}`, api => api.accounts.getUser(result.user.id));
                return this.admin.applyUpdate(api => api.accounts.updateUser(user.id, { [this.filterField]: this.section }));
            }
        }
        if (action === 'delete') {
            const update = await this.getReplacementGroupFn();
            if (!update) { return null; }

            return this.admin.applyUpdate(api => api.accounts.updateUser(items[0].id, { [this.filterField]: update }));
        }
    }


}
